import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, ArrowUpRight, CalendarClock, Box } from "lucide-react";
import { scrollToId, stopScroll, startScroll } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const BADGE_STYLES = {
  "In-House Flagship": "bg-[#263562]/10 text-[#263562] border-[#263562]/25",
  "HTI Distribution": "bg-[#3AB0E1]/10 text-[#1E8FBF] border-[#3AB0E1]/30",
  "Distribution Partner": "bg-[#949CA1]/10 text-[#5F6B78] border-[#949CA1]/30",
};

export default function ProductModal({ product, onClose }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    setActive(0);
    if (!product) return;
    stopScroll();
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      startScroll();
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [product, onClose]);

  const requestDemo = () => {
    window.dispatchEvent(new CustomEvent("prefill-product", { detail: product.name }));
    onClose();
    setTimeout(() => scrollToId("#contact"), 60);
  };

  const gallery = product?.gallery || [];

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          data-testid="product-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-8 bg-[#1B2647]/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-[0_40px_100px_-20px_rgba(27,38,71,0.5)]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="product-modal-close"
              onClick={onClose}
              aria-label="Close product details"
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white border border-[#263562]/10 shadow-md flex items-center justify-center text-[#263562] hover:bg-[#263562] hover:text-white transition-colors duration-300"
            >
              <X className="w-4.5 h-4.5 w-[18px] h-[18px]" />
            </button>

            <div className="grid md:grid-cols-2">
              <div className="bg-[#F5F8FC] p-6 sm:p-8 flex flex-col">
                {gallery.length > 0 ? (
                  <>
                    <div className="rounded-xl overflow-hidden border border-[#263562]/10 bg-white flex items-center justify-center min-h-[280px]">
                      <AnimatePresence mode="wait">
                        <motion.img
                          key={gallery[active].src}
                          data-testid="product-modal-gallery-image"
                          src={gallery[active].src}
                          alt={gallery[active].caption}
                          initial={{ opacity: 0, scale: 0.98 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.35 }}
                          className="w-full h-auto max-h-[340px] object-contain"
                        />
                      </AnimatePresence>
                    </div>
                    <p className="mt-3 text-center text-xs text-[#5A6B85] font-light">
                      {gallery[active].caption}
                    </p>
                    {gallery.length > 1 && (
                      <div className="mt-4 flex gap-2.5 overflow-x-auto pb-1">
                        {gallery.map((g, i) => (
                          <button
                            key={g.src}
                            data-testid={`product-modal-thumb-${i}`}
                            onClick={() => setActive(i)}
                            className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 bg-white transition-all duration-300 ${
                              i === active
                                ? "border-[#3AB0E1] shadow-md"
                                : "border-[#263562]/10 opacity-60 hover:opacity-100"
                            }`}
                          >
                            <img src={g.src} alt="" className="w-full h-full object-cover" loading="lazy" />
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex-1 rounded-xl border border-[#263562]/10 bg-[radial-gradient(circle_at_35%_25%,rgba(58,176,225,0.14),transparent_60%)] flex flex-col items-center justify-center min-h-[280px] gap-4">
                    <span className="font-display font-bold text-7xl text-stroke-navy">
                      {product.name.split(" ")[0].slice(0, 2).toUpperCase()}
                    </span>
                    <span className="flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#949CA1]">
                      <Box className="w-3.5 h-3.5" />
                      Demo available on request
                    </span>
                  </div>
                )}
              </div>

              <div className="p-6 sm:p-10">
                <span
                  className={`inline-block rounded-full border px-3 py-1 font-mono-tech text-[9px] uppercase tracking-[0.2em] ${BADGE_STYLES[product.badge]}`}
                >
                  {product.badge}
                </span>
                <h3 className="mt-4 font-display font-semibold tracking-tight text-3xl sm:text-4xl text-[#263562]">
                  {product.name}
                </h3>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#5A6B85] font-light">
                  {product.description}
                </p>

                <h4 className="mt-7 font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#263562]/60">
                  Key Capabilities
                </h4>
                <ul className="mt-4 space-y-2.5">
                  {product.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-3 text-sm text-[#3D4E6E]">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-[#3AB0E1]/15 border border-[#3AB0E1]/50 flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-[#1E8FBF]" />
                      </span>
                      {h}
                    </li>
                  ))}
                </ul>

                <h4 className="mt-7 font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#263562]/60">
                  Ideal For
                </h4>
                <div className="mt-3 flex flex-wrap gap-2">
                  {product.idealFor.map((t) => (
                    <span
                      key={t}
                      className="rounded-full bg-[#F5F8FC] border border-[#263562]/10 px-3.5 py-1.5 text-xs text-[#263562]"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                <div className="mt-9 flex flex-wrap items-center gap-4">
                  <button
                    data-testid="product-modal-demo-button"
                    onClick={requestDemo}
                    className="group flex items-center gap-2.5 bg-[#263562] hover:bg-[#3AB0E1] text-white font-semibold px-6 py-3.5 rounded-full transition-all duration-300 hover:shadow-[0_10px_35px_rgba(58,176,225,0.4)]"
                  >
                    <CalendarClock className="w-4 h-4" />
                    Request a Demo
                  </button>
                  <a
                    data-testid="product-modal-site-link"
                    href={product.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#263562] hover:text-[#3AB0E1] transition-colors"
                  >
                    Official product site
                    <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
